/**
 * Auth trace: a durable, per-user record of what the login path actually did.
 *
 * The failures this exists for are invisible from the server side — "the browser showed the QR
 * instead of my passkey", "the prompt never appeared", "it worked yesterday". The only evidence is
 * on the client, and it has to survive the user closing the tab, so every event is recorded to a
 * bounded ring buffer in `localStorage` **regardless of whether the console is on**. Console output
 * is the noisy part and is opt-in (`?debug=1`).
 *
 * Linking an activity to a moment is the point: each page load gets a `trace` id (kept across
 * reloads in the same tab), every event carries an ISO timestamp and the email once it is known,
 * so a report can be read as "this user, this trace, this window".
 *
 * What is recorded is public material — credential ids, PRF salts, device ids, timings, provider
 * names. No PRF output, no master seed, no session token ever reaches this module.
 */

import {
  base64ToBytes,
  resolveEffectiveRpId,
  type CeremonyDebug,
  type WrappedDeviceMasterKey,
} from "@tensilelabs/auth-client";

const PREFIX = "[taskjinn-auth]";
const TRACE_KEY = "tensile:auth-trace";
const TRACE_ID_KEY = "tensile:auth-trace-id";
const FLAG_KEY = "tensile:auth-debug";
/** Enough for a full login plus a couple of retries, small enough to stay under a 5 MB quota. */
const MAX_EVENTS = 400;

export interface AuthTraceEvent {
  /** ISO-8601, so a report can be lined up with server logs. */
  at: string;
  ms: number;
  /** Page-load id; survives reloads in the same tab so one activity reads as one trace. */
  trace: string;
  /** The account the event belongs to, once it is known. */
  user: string | null;
  event: string;
  data?: Record<string, unknown>;
}

export interface AuthTrace {
  traceId: string;
  user: string | null;
  recordedAt: string;
  events: AuthTraceEvent[];
}

let currentUser: string | null = null;

/**
 * Console output, off by default.
 *
 * `?debug=1` turns it on for one visit; `?debug=0` forces it off even if the flag is set. The
 * stored flag exists so a user can be told "run this once" without editing URLs.
 */
export function authDebugEnabled(): boolean {
  try {
    const param = new URLSearchParams(window.location.search).get("debug");
    if (param === "1") return true;
    if (param === "0") return false;
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

function traceId(): string {
  try {
    const existing = sessionStorage.getItem(TRACE_ID_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(TRACE_ID_KEY, created);
    return created;
  } catch {
    return "no-storage";
  }
}

function readEvents(): AuthTraceEvent[] {
  try {
    const raw = localStorage.getItem(TRACE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AuthTraceEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeEvents(events: AuthTraceEvent[]): void {
  try {
    localStorage.setItem(TRACE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch {
    // Quota or private mode: the console (when enabled) is all that is left.
  }
}

/** Attributes subsequent events to an account. Call as soon as the email is known. */
export function setDebugUser(email: string | null): void {
  currentUser = email ? email.trim().toLowerCase() : null;
}

/**
 * Records an event, and echoes it to the console when `?debug=1`.
 *
 * Recording never throws: a browser that blocks storage must not break a login.
 */
export function authDebug(event: string, data?: Record<string, unknown>): void {
  const entry: AuthTraceEvent = {
    at: new Date().toISOString(),
    ms: Date.now(),
    trace: traceId(),
    user: currentUser,
    event,
    ...(data ? { data } : {}),
  };

  writeEvents([...readEvents(), entry]);

  if (!authDebugEnabled()) return;
  if (data) console.info(PREFIX, event, data);
  else console.info(PREFIX, event);
}

/** The sink the SDK ceremonies report through. */
export const ceremonyDebug: CeremonyDebug = (event, data) => authDebug(event, data);

function byteLengthOrReason(b64: string): number | string {
  try {
    return base64ToBytes(b64).byteLength;
  } catch {
    return `invalid base64: ${b64.slice(0, 16)}…`;
  }
}

/**
 * A device envelope as it appears on the wire.
 *
 * `credentialIdB64` is the value that goes into `allowCredentials`; a browser can only satisfy the
 * ceremony when one of these is the credential it actually holds. `credentialIdBytes` catches a
 * malformed record (base64url, truncated, or an id that was never a credential).
 */
export function describeEnvelope(envelope: WrappedDeviceMasterKey): Record<string, unknown> {
  return {
    deviceId: envelope.device_id,
    deviceName: envelope.device_name,
    credentialIdB64: envelope.credential_id_b64,
    credentialIdBytes: byteLengthOrReason(envelope.credential_id_b64),
    prfSaltB64: envelope.prf_salt_b64,
    wrappedSeedBytes: byteLengthOrReason(envelope.wrapped_seed_b64),
  };
}

/** What this browser can do and which rpId the ceremonies will use. */
export async function describeClientCapabilities(): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {
    hostname: window.location.hostname,
    origin: window.location.origin,
    secureContext: window.isSecureContext,
    resolvedRpId: resolveEffectiveRpId("tensilelabs.com"),
    userAgent: navigator.userAgent,
    hasPublicKeyCredential: typeof window.PublicKeyCredential !== "undefined",
    hasCredentialsGet: Boolean(navigator.credentials?.get),
    hasCredentialsCreate: Boolean(navigator.credentials?.create),
  };

  const api = window.PublicKeyCredential as unknown as
    | {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
        isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
      }
    | undefined;

  try {
    if (api?.getClientCapabilities) {
      out.clientCapabilities = await api.getClientCapabilities();
    }
    if (api?.isUserVerifyingPlatformAuthenticatorAvailable) {
      out.platformAuthenticatorAvailable =
        await api.isUserVerifyingPlatformAuthenticatorAvailable();
    }
  } catch (cause) {
    out.capabilityError = cause instanceof Error ? cause.message : String(cause);
  }

  return out;
}

/**
 * The device profile, captured once per page load.
 *
 * "It fails on my machine" is unanswerable without this: the same ceremony behaves differently on
 * macOS Chrome, Windows Edge, an iPhone and an in-app WebView, and the passkey provider differs per
 * platform. User-Agent Client Hints are used where they exist (Chrome/Edge) because the UA string
 * alone no longer names the OS version.
 */
let deviceProfile: Record<string, unknown> | null = null;

export async function describeDevice(): Promise<Record<string, unknown>> {
  if (deviceProfile) return deviceProfile;

  const nav = navigator as Navigator & {
    userAgentData?: {
      brands?: Array<{ brand: string; version: string }>;
      platform?: string;
      mobile?: boolean;
      getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
    };
    deviceMemory?: number;
  };

  const profile: Record<string, unknown> = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    vendor: navigator.vendor,
    language: navigator.language,
    languages: navigator.languages,
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return null;
      }
    })(),
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    deviceMemory: nav.deviceMemory ?? null,
    maxTouchPoints: navigator.maxTouchPoints ?? null,
    screen: {
      width: window.screen?.width ?? null,
      height: window.screen?.height ?? null,
      availWidth: window.screen?.availWidth ?? null,
      availHeight: window.screen?.availHeight ?? null,
      colorDepth: window.screen?.colorDepth ?? null,
      devicePixelRatio: window.devicePixelRatio ?? null,
    },
    prefersDark: (() => {
      try {
        return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? null;
      } catch {
        return null;
      }
    })(),
  };

  if (nav.userAgentData) {
    profile.clientHints = {
      brands: nav.userAgentData.brands,
      platform: nav.userAgentData.platform,
      mobile: nav.userAgentData.mobile,
    };
    try {
      if (nav.userAgentData.getHighEntropyValues) {
        // Architecture and OS version are the two that actually identify a platform.
        profile.highEntropy = await nav.userAgentData.getHighEntropyValues([
          "platformVersion",
          "architecture",
          "bitness",
          "model",
          "uaFullVersion",
        ]);
      }
    } catch {
      // Optional hints; their absence is not an error.
    }
  }

  deviceProfile = profile;
  return profile;
}

/** Everything recorded so far, newest last. */
export function readAuthTrace(): AuthTrace {
  const events = readEvents();
  return {
    traceId: traceId(),
    user: currentUser ?? events[events.length - 1]?.user ?? null,
    recordedAt: new Date().toISOString(),
    events,
  };
}

/**
 * Writes the trace to a local file.
 *
 * A file rather than a paste: the point is to keep it for a later report, and the name carries the
 * account and the moment so several of them stay distinguishable.
 */
export function downloadAuthTrace(): void {
  const trace = readAuthTrace();
  const stamp = trace.recordedAt.replace(/[:.]/g, "-");
  const who = (trace.user ?? "anonymous").replace(/[^a-z0-9@._-]/gi, "_");
  const blob = new Blob([JSON.stringify(trace, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `taskjinn-auth-${who}-${stamp}.json`;
  link.click();
  URL.revokeObjectURL(url);
  authDebug("trace:downloaded", { events: trace.events.length });
}

/**
 * Uploads the trace and returns the reference to quote.
 *
 * Called on failure only: a working login needs no support ticket, and uploading every ceremony
 * would turn a debugging aid into a data collection. Best effort — a failed upload must never
 * change what the page reports.
 */
export async function uploadAuthTrace(error: string): Promise<string | null> {
  try {
    const trace = readAuthTrace();
    const response = await fetch("/api/v1/telemetry/auth-trace", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email: trace.user ?? "",
        error,
        context: await describeDevice(),
        events: trace.events,
      }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { reference?: string };
    const reference = body.reference ?? null;
    authDebug("trace:uploaded", { reference, events: trace.events.length });
    return reference;
  } catch {
    return null;
  }
}

export function clearAuthTrace(): void {
  try {
    localStorage.removeItem(TRACE_KEY);
    sessionStorage.removeItem(TRACE_ID_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/** Turns the console on for this browser without needing the query parameter. */
export function enableAuthDebug(): void {
  try {
    localStorage.setItem(FLAG_KEY, "1");
  } catch {
    // Storage disabled: `?debug=1` is the only way.
  }
}

/** `window.tjAuthTrace` — read, download or clear the record. */
export function installAuthTraceHelpers(): void {
  const helpers = {
    read: readAuthTrace,
    download: downloadAuthTrace,
    clear: clearAuthTrace,
    enable: enableAuthDebug,
  };
  (window as unknown as { tjAuthTrace?: typeof helpers }).tjAuthTrace = helpers;
  authDebug("trace:installed", {
    console: "?debug=1",
    api: "window.tjAuthTrace.{read,download,clear,enable}",
  });
}
